import { useCallback, useEffect, useRef, useState } from "react";

import { readHumanErrorMessage } from "@/shared/utils/apiError";

import { CardholderOnboardingService } from "../services/cardholderOnboarding.service";
import type {
  VerificationDiscoveryResult,
  VerificationDiscoveryRun,
} from "../types/cardholderOnboarding.types";

export type VerificationLinkPhase =
  | "searching"
  | "found"
  | "not_found"
  | "other_identity"
  | "linking"
  | "linked"
  | "linked_elsewhere"
  | "error";

interface Lookup {
  applicantId: string | null;
  result: VerificationDiscoveryResult | null;
}

/**
 * Finds the verification of one cardholder and links it. First by the identity itself (the provider holds
 * applicants under the identity id); staff can also paste the id of a verification made outside the platform.
 * Only a `linkable` match for THIS identity is ever applied — the run links whatever is linkable in its
 * scope, so a pasted id that resolves to somebody else is shown and never sent.
 */
export function useVerificationLink(identityId: string, fallbackError: string, enabled = true) {
  const [phase, setPhase] = useState<VerificationLinkPhase>(enabled ? "searching" : "not_found");
  const [lookup, setLookup] = useState<Lookup>({ applicantId: null, result: null });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const searchedRef = useRef(false);

  const run = useCallback(
    (applicantId: string | null, dryRun: boolean) =>
      CardholderOnboardingService.discoverVerification(
        applicantId
          ? { applicant_ids: [applicantId], dry_run: dryRun }
          : { identity_ids: [identityId], dry_run: dryRun },
      ),
    [identityId],
  );

  const showLookup = useCallback(
    (applicantId: string | null, response: VerificationDiscoveryRun) => {
      const mine = response.results.find((row) => row.identity_id === identityId);
      const result = mine ?? response.results[0] ?? null;
      setLookup({ applicantId, result });
      if (mine?.category === "linkable") setPhase("found");
      else if (result?.category === "linkable") setPhase("other_identity");
      else setPhase("not_found");
    },
    [identityId],
  );

  const showFailure = useCallback(
    (err: unknown) => {
      setErrorMessage(readHumanErrorMessage(err, fallbackError));
      setPhase("error");
    },
    [fallbackError],
  );

  useEffect(() => {
    if (!enabled || searchedRef.current) return;
    searchedRef.current = true;
    run(null, true).then((response) => showLookup(null, response), showFailure);
  }, [enabled, run, showLookup, showFailure]);

  const search = useCallback(
    async (applicantId: string | null) => {
      setPhase("searching");
      setErrorMessage(null);
      try {
        showLookup(applicantId, await run(applicantId, true));
      } catch (err) {
        showFailure(err);
      }
    },
    [run, showLookup, showFailure],
  );

  const link = useCallback(async (): Promise<boolean> => {
    setPhase("linking");
    setErrorMessage(null);
    try {
      const response = await run(lookup.applicantId, false);
      const mine = response.results.find((row) => row.identity_id === identityId) ?? null;
      setLookup((current) => ({ ...current, result: mine }));
      if (mine?.category === "linked") {
        setPhase("linked");
        return true;
      }
      // The real run re-matches the pasted id; if it now resolved to somebody else it may have linked them.
      const elsewhere = response.results.some(
        (row) => row.category === "linked" && row.identity_id !== identityId,
      );
      setPhase(elsewhere ? "linked_elsewhere" : "not_found");
      return elsewhere;
    } catch (err) {
      showFailure(err);
      return false;
    }
  }, [run, lookup.applicantId, identityId, showFailure]);

  return { phase, result: lookup.result, usedApplicantId: lookup.applicantId, errorMessage, search, link };
}
